import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EditForm from "./EditFormBuilder";
import "../css/ParticipantList.css";
import "../css/FormList.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faTrashAlt, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import axios from "axios";
import DeleteConfirmModal, { useDeleteConfirm } from "./DeleteConfirmModal";
import { GoldLoader } from "../shared/Loaders";

const FormList = () => {
  const [forms, setForms] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [editForm, setEditForm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const deleteConfirm = useDeleteConfirm();
  const API_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  const fetchForms = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/ems/forms`);
      const data = res.data?.data || res.data;
      setForms(Array.isArray(data) ? data : []);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to fetch forms", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, []);

  const handleDelete = (form) => {
    deleteConfirm.ask({ id: form._id, name: form.form_name || "this form" });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.pending?.id;
    if (!id) return;
    deleteConfirm.setBusy(true);
    try {
      await axios.delete(`${API_URL}/ems/forms/${id}`);
      deleteConfirm.clear();
      fetchForms();
    } catch (err) {
      console.error(err);
      deleteConfirm.setError("Delete failed");
      deleteConfirm.setBusy(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleEdit = (form) => {
    setEditForm(form);
  };

  // Filter forms by search term
  const filteredForms = forms.filter((f) =>
    (f.form_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const sorted = sortConfig.key
    ? [...filteredForms].sort((a, b) => {
        if (sortConfig.key === 'createdAt') {
          const aDate = new Date(a.createdAt || 0).getTime();
          const bDate = new Date(b.createdAt || 0).getTime();
          return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
        }
        const aVal = (a.form_name || '').toLowerCase();
        const bVal = (b.form_name || '').toLowerCase();
        const result = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? result : -result;
      })
    : filteredForms;

  // Pagination calculations
  const effectivePerPage = itemsPerPage >= sorted.length ? Math.max(sorted.length, 1) : itemsPerPage;
  const totalPages = Math.max(Math.ceil(sorted.length / effectivePerPage), 1);
  const indexOfLast = currentPage * effectivePerPage;
  const indexOfFirst = indexOfLast - effectivePerPage;
  const currentForms = sorted.slice(indexOfFirst, indexOfLast);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const getVisiblePages = () => {
    if (totalPages <= 5) return pageNumbers;
    if (currentPage <= 3) return pageNumbers.slice(0, 5);
    if (currentPage >= totalPages - 2) return pageNumbers.slice(-5);
    return pageNumbers.slice(currentPage - 3, currentPage + 2);
  };

  if (isLoading) {
    return <GoldLoader label="Loading..." variant="page" />;
  }

  return (
    <div className="ems-forms-page p-4">
      {/* Header with Search on left and Add Button on right */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 ems-list-toolbar">
        <div className="d-flex align-items-center w-25">
          <div className="me-2" style={{ flex: "1" }}>
            <input
              type="text"
              className="form-control form-control-sm search-input"
              placeholder="Search forms..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              title="Search forms"
              style={{ border: "1px solid #d1cfcf" }}
            />
          </div>
        </div>
        <button
          onClick={() => navigate("/ems/forms/add")}
          style={{
            background: '#000',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '5px',
            border: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          + Add Form
        </button>
      </div>

      {/* Table */}
        <div className="forms-table-header">
          <div
            className={`sort-header ${sortConfig.key === 'form_name' ? 'sorted' : ''}`}
            onClick={() => toggleSort('form_name')}
          >
            Form Name <FontAwesomeIcon icon={sortIcon('form_name')} className="sort-icon" />
          </div>
          <div
            className={`sort-header ${sortConfig.key === 'createdAt' ? 'sorted' : ''}`}
            onClick={() => toggleSort('createdAt')}
          >
            Created At <FontAwesomeIcon icon={sortIcon('createdAt')} className="sort-icon" />
          </div>
          <div>Actions</div>
        </div>

        {isLoading ? (
          <GoldLoader label="Loading..." variant="section" />
        ) : sorted.length === 0 ? (
          <div className="p-4 text-center text-muted">
            No forms available
          </div>
        ) : (
          currentForms.map((f) => (
            <React.Fragment key={f._id}>
              <div className="forms-table-row">
                <div>
                  <span
                    onClick={() => handleEdit(f)}
                    style={{ cursor: "pointer", fontWeight: 500 }}
                  >
                    {f.form_name || "Unnamed"}
                  </span>
                </div>
                <div>
                  {f.createdAt
                    ? new Date(f.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "-"}
                </div>
                <div className="forms-actions">
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => navigate(`/ems/forms/view/${f._id}`)}
                    title="View Form"
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => handleEdit(f)}
                    title="Edit Form"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button
                    className="btn btn-sm action-btn"
                    onClick={() => handleDelete(f)}
                    title="Delete Form"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === f._id && (
                <div className="forms-details p-3 border rounded bg-light mb-2">
                  <p><strong>Form Name:</strong> {f.form_name || "-"}</p>
                  <p>
                    <strong>Schema:</strong>
                    <pre style={{ whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(f.schema, null, 2) || "-"}
                    </pre>
                  </p>
                  <p><strong>Created At:</strong> {f.createdAt ? new Date(f.createdAt).toLocaleString() : "-"}</p>
                  <p><strong>Updated At:</strong> {f.updatedAt ? new Date(f.updatedAt).toLocaleString() : "-"}</p>
                </div>
              )}
            </React.Fragment>
          ))
        )}

        {/* Pagination inside table */}
        {sorted.length > 0 && (
          <div className="participant-pagination">
            <div className="d-flex align-items-center gap-2">
              <span style={{ fontSize: '13px', color: '#666' }}>Show</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: '13px' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="pagination-box">
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>

              {getVisiblePages().map((num) => (
                <button
                  key={num}
                  className={`pagination-btn ${num === currentPage ? 'active' : ''}`}
                  onClick={() => setCurrentPage(num)}
                >
                  {num}
                </button>
              ))}

              <button
                className="pagination-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

      {/* Edit Form Modal */}
      {editForm && (
        <EditForm
          formData={editForm}
          isOpen={!!editForm}
          onClose={() => setEditForm(null)}
          onSaved={fetchForms}
        />
      )}
      {deleteConfirm.pending && (
        <DeleteConfirmModal
          name={deleteConfirm.pending.name}
          error={deleteConfirm.error}
          busy={deleteConfirm.busy}
          onConfirm={confirmDelete}
          onCancel={deleteConfirm.clear}
        />
      )}
    </div>
  );
};

export default FormList;
